// src/views/writing/WritingView.tsx
import React, {
  FC, memo, useEffect, useState, useRef, useCallback, useMemo,
} from 'react';
import './WritingView.less';
import './slice/slice';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import { ReactComponent as Repeat } from '../../assets/icons/games/writing/refresh-ccw-rec.svg';
import { ReactComponent as NextAnyway } from '../../assets/icons/games/writing/arrow-right-rec.svg';
import FixWidthContent from '../../components/FixWidthContent';
import withTagManager from '../../hooks/withTagManager';
import { WordFromList } from '../../components/Writing/WordFromList';
import InfoButton from '../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import { getActiveList, getUserData } from '../auth/slice/selectors';
import { RightSideButtons } from '../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../components/UnsplashCredits/UnsplashCredits';
import EndGameInfo from '../../components/EndGameInfo/EndgameInfo';
import { EditTermButton } from '../../components/EditTermModal/EditTermButton';
import { EditListButton } from '../../components/EditListButton/EditListButton';
import { usePolishAudio } from '../../hooks/usePolishAudio';
import { PolishAudioButton } from '../../components/PolishAudioButton/PolishAudioButton';
import { Roles } from '../../constants/Roles';
import { cleanTextForTTS } from '../../utils/textBracketSanitize';
import { getListComposition, getListWordId, withListTexts } from '../../utils/gameWords';
import WordCounter from '../../components/WordCounter';
import useActivityTracking from '../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../services/activity';

// Configuration constants
const SEPARATOR_CONFIG = {
  MAX_ATTEMPTS: 50,
  PREFERRED_ATTEMPTS_THRESHOLD: 30,
  PREFERRED_RANGE_RATIO: 0.7,
};

export const WritingView: FC = memo(() => {
  const { t } = useTranslation();
  const activeList = useSelector(getActiveList);
  const { words } = activeList || { words: [] };

  const wordsCopy = useMemo(() => words.map((w: any) => ({ ...w })), [words]);

  const [currentWords, setCurrentWords] = useState(() => [...words]);
  const [wordOnScreen, setWordOnScreen] = useState(0);
  const [nextWordButton, setNextWordButton] = useState(false);
  const [feedbackInfo, setFeedbackInfo] = useState<string | React.ReactNode>('');
  const [listEndInfo, setListEndInfo] = useState(false);
  const [tryAgainButton, setTryAgainButton] = useState<boolean>(false);
  const [inputContent, setInputContent] = useState<string>('');
  const [isTransitioning, setIsTransitioning] = useState(false);

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  // Próby po Enterze zapisuje pole wpisywania (WritingInput), a SKIP — handleNextAnywayButton
  useActivityTracking(ActivityGame.WRITING);

  const {
    playPolish,
    togglePlayPolish,
    playPolishAudio,
    stopPolishAudio,
  } = usePolishAudio({ storageKey: 'writing_playPolish' });

  const autoAdvanceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const generateUniqueId = useCallback(() => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }, []);

  const getRandomSeparatorWord = useCallback((excludeTerm: string) => {
    const { MAX_ATTEMPTS, PREFERRED_ATTEMPTS_THRESHOLD, PREFERRED_RANGE_RATIO } = SEPARATOR_CONFIG;

    let randomWordObj;
    let attempts = 0;

    do {
      const preferredRange = attempts < PREFERRED_ATTEMPTS_THRESHOLD
        ? Math.ceil(wordsCopy.length * PREFERRED_RANGE_RATIO)
        : wordsCopy.length;

      const randomIndex = Math.floor(Math.random() * preferredRange);
      randomWordObj = wordsCopy[randomIndex];
      attempts += 1;
    } while (
      attempts < MAX_ATTEMPTS
      && randomWordObj.term === excludeTerm
    );

    // Defensive check
    if (randomWordObj.term === excludeTerm) {
      console.warn('Could not find different separator word after max attempts');
    }

    return randomWordObj;
  }, [wordsCopy]);

  const numberOfWords = currentWords.length;
  const index = wordOnScreen + 1;

  useEffect(() => {
    if (!listEndInfo && currentWords && currentWords[wordOnScreen]) {
      const { definition } = currentWords[wordOnScreen];
      playPolishAudio(definition);
    }

    return () => {
      stopPolishAudio();
    };
  }, [wordOnScreen, listEndInfo, currentWords, playPolishAudio, stopPolishAudio]);

  // Gra zaczyna od nowa przy innym składzie listy (inna lista, dodane albo usunięte słowa). Poprawka tekstu,
  // np. z „Edytuj zwrot”, gry nie przerywa: słowa w grze dostają nowy tekst, a gra zostaje na tym samym słowie
  const listCompositionRef = useRef('');

  useEffect(() => {
    const listComposition = getListComposition(words);

    if (listComposition === listCompositionRef.current) {
      setCurrentWords((prev) => withListTexts(prev, words));

      return;
    }

    listCompositionRef.current = listComposition;
    setCurrentWords([...words]);
    setWordOnScreen(0);
    setFeedbackInfo('');
    setNextWordButton(false);
    setTryAgainButton(false);
    setInputContent('');
    setListEndInfo(false);
    setIsTransitioning(false);
  }, [words]);

  const repeatGame = useCallback(() => {
    stopPolishAudio();

    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }

    setListEndInfo(false);
    setWordOnScreen(0);
    setFeedbackInfo('');
    setCurrentWords([...wordsCopy]);
    setNextWordButton(false);
    setTryAgainButton(false);
    setInputContent('');
    setIsTransitioning(false);
  }, [stopPolishAudio, wordsCopy]);

  const addWordToRepeat = useCallback(() => {
    const currentWord = currentWords[wordOnScreen];
    if (!currentWord) return;

    const wordTerm = currentWord.term;

    // Check if already scheduled for repeat
    const remainingWords = currentWords.slice(wordOnScreen + 1);
    if (remainingWords.some((word) => word.term === wordTerm)) {
      return;
    }

    type WordType = typeof currentWord;
    const wordsToAdd: WordType[] = [];

    // Determine if separator is needed
    const needsSeparator = wordOnScreen === currentWords.length - 1
      || currentWords[wordOnScreen + 1]?.term === wordTerm;

    // Add separator if needed
    if (needsSeparator) {
      const separatorWord = getRandomSeparatorWord(wordTerm);
      wordsToAdd.push({
        ...separatorWord,
        index: currentWords.length + wordsToAdd.length,
        id: `sep_${generateUniqueId()}`,
        listWordId: getListWordId(separatorWord),
      });
    }

    // Add word to repeat
    wordsToAdd.push({
      ...currentWord,
      index: currentWords.length + wordsToAdd.length,
      id: `repeat_${generateUniqueId()}`,
      listWordId: getListWordId(currentWord),
    });

    setCurrentWords((prev) => [...prev, ...wordsToAdd]);
  }, [currentWords, wordOnScreen, getRandomSeparatorWord, generateUniqueId]);

  const handleNextWordButton = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    stopPolishAudio();

    if (index < numberOfWords) {
      setWordOnScreen((prev) => prev + 1);
      setFeedbackInfo('');
      setInputContent('');
      setNextWordButton(false);
      setTryAgainButton(false);
      setIsTransitioning(false);
    } else if (index === numberOfWords) {
      setListEndInfo(true);
      setNextWordButton(false);
      setIsTransitioning(false);
    }
  }, [isTransitioning, stopPolishAudio, index, numberOfWords]);

  const tryAgain = useCallback(() => {
    if (isTransitioning) return;

    addWordToRepeat();

    setFeedbackInfo('');
    setTryAgainButton(false);
    setNextWordButton(false);
    setInputContent('');
  }, [isTransitioning, addWordToRepeat]);

  const handleNextAnywayButton = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    // SKIP (widoczny do Entera) to próba z wynikiem `false`. I WAS RIGHT (po błędnej odpowiedzi) woła tę samą funkcję,
    // ale próbą nie jest — zostaje wynik sprawdzenia, inaczej szybkie klikanie I WAS RIGHT dawałoby same dobre odpowiedzi
    if (!tryAgainButton) {
      recordAnswer(false, currentWords[wordOnScreen]?.term);
    }

    stopPolishAudio();

    const clearingSteps = () => {
      setFeedbackInfo('');
      setTryAgainButton(false);
      setNextWordButton(false);
      setInputContent('');
    };

    if (wordOnScreen < currentWords.length - 1) {
      setWordOnScreen((prev) => prev + 1);
      clearingSteps();
      setIsTransitioning(false);
    } else {
      setListEndInfo(true);
      clearingSteps();
      setIsTransitioning(false);
    }
  }, [isTransitioning, tryAgainButton, stopPolishAudio, wordOnScreen, currentWords]);

  useEffect(() => {
    if (feedbackInfo && nextWordButton) {
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
      }

      autoAdvanceTimeoutRef.current = setTimeout(() => {
        handleNextWordButton();
        autoAdvanceTimeoutRef.current = null;
      }, 1000);
    }

    return () => {
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
        autoAdvanceTimeoutRef.current = null;
      }
    };
  }, [feedbackInfo, nextWordButton, handleNextWordButton]);

  useEffect(() => () => {
    stopPolishAudio();
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
    }
  }, [stopPolishAudio]);

  if (!currentWords || currentWords.length === 0) {
    return (
      <FixWidthContent className="writing-main">
        <div>{t('games:states.noWords')}</div>
      </FixWidthContent>
    );
  }

  const current = currentWords[wordOnScreen];
  const cleanedTerm = current ? cleanTextForTTS(current.term) : '';

  return (
    <>
      <FixWidthContent className="writing-main">
        {!listEndInfo && (
          <WordCounter
            current={wordOnScreen + 1}
            total={currentWords.length}
          />
        )}
        {/* Gra w zwykłym bloku — .writing-main jest kolumną flex, żeby SKIP mógł stanąć na dole ekranu */}
        {!listEndInfo && current && (
          <div>
            <WordFromList
              id={current.id}
              content={cleanedTerm}
              term={current.term}
              translation={current.definition}
              image={current.image}
              setNextWordButton={setNextWordButton}
              setFeedbackInfo={setFeedbackInfo}
              setTryAgainButton={setTryAgainButton}
              tryAgainButton={tryAgainButton}
              tryAgain={tryAgain}
              setInputContent={setInputContent}
              inputContent={inputContent}
              answeredCorrectly={nextWordButton}
            />

            <div id="feedback" aria-live="polite">{feedbackInfo}</div>

            <div className="optionsContainer">
              {tryAgainButton && (
                <>
                  <Button
                    className="clearButton"
                    onClick={tryAgain}
                    disabled={isTransitioning}
                    aria-label={t('games:labels.clearAndRetry')}
                  >
                    <Repeat className="btnLabel" />
                    <br />
                    {t('games:buttons.clear')}
                  </Button>

                  <Button
                    className="nextButton"
                    onClick={handleNextAnywayButton}
                    disabled={isTransitioning}
                    aria-label={t('games:labels.iWasRightNext')}
                  >
                    <NextAnyway className="btnLabel" />
                    <br />
                    {t('games:buttons.iWasRight')}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* SKIP widać do Entera; po sprawdzeniu jest już kciuk albo CLEAR i I WAS RIGHT. Stoi na dole ekranu,
            na środku, żeby nie zachęcał do pomijania słów — jak w Napisz Easy */}
        {!listEndInfo && current && !tryAgainButton && !nextWordButton && (
          <div className="writing-skip">
            <Button
              className="nextButton"
              onClick={handleNextAnywayButton}
              disabled={isTransitioning}
              aria-label={t('games:labels.skipWord')}
            >
              <NextAnyway className="btnLabel" />
              <br />
              {t('games:buttons.skip')}
            </Button>
          </div>
        )}

        {/* Ekran końcowy w zwykłym bloku, żeby jego odstępy zostały jak dotąd */}
        {listEndInfo && (
          <div>
            <EndGameInfo repeatGameTrigger={repeatGame} bigCongrats />
          </div>
        )}

        {!listEndInfo && <UnsplashCredits words={current ? [current] : []} />}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          {canEditTerms && current && (
            <EditTermButton
              wordId={getListWordId(current)}
              currentTerm={current.term}
              currentDefinition={current.definition}
              listId={activeList?.id || ''}
              onTermUpdated={(newTerm, newDefinition) => {
                const listWordId = getListWordId(current);

                setCurrentWords((prev) => prev.map((word) => (getListWordId(word) === listWordId
                  ? { ...word, term: newTerm, definition: newDefinition }
                  : word)));
              }}
            />
          )}

          <PolishAudioButton
            playPolish={playPolish}
            onToggle={togglePlayPolish}
          />

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}

        </RightSideButtons>
      </FixWidthContent>
    </>
  );
});

export default withTagManager(WritingView);
